import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerOrderDetail } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerOrders } from "@/lib/query/core/invalidation";

type UndoOrderStatusInput = {
  storeSlug: string;
  orderId: string;
};

/**
 * Reverts an order to its previous status (server reads order_status_history).
 * The seller-orders caches are fully invalidated afterwards because an undo can
 * shift bucket membership and re-reserve stock.
 */
export function useUndoOrderStatusMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation<SellerOrderDetail, Error, UndoOrderStatusInput>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ orderId }: UndoOrderStatusInput) =>
      apiJsonClient<SellerOrderDetail>(
        endpoints.seller.orderStatusUndo(orderId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      ),
    onSettled: async (_data, _error, { storeSlug }) => {
      await invalidateSellerOrders(queryClient, storeSlug);
    },
  });

  return { mutateAsync: mutation.mutateAsync };
}
